import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { storage } from "@/lib/storage";
import { createRecord, computeHash, verifyChain, GENESIS_HASH } from "@/lib/crypto";
import { generateIdentity, signHash, verifySignature, isValidIdentity, importIdentity as importSigningIdentity, clearSigningKey } from "@/lib/signing";
import { seedDemo } from "@/lib/seed";

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export function AppProvider({ children }) {
  const [records, setRecords] = useState([]);
  const [integrity, setIntegrity] = useState([]);
  const [settings, setSettings] = useState(storage.loadSettings());
  const [hct3, setHct3] = useState(storage.loadHct3());
  const [identity, setIdentity] = useState(() => {
    const i = storage.loadIdentity();
    if (!isValidIdentity(i)) return null;
    // Never hold private material in memory/state — public descriptor only.
    return { label: i.label, algorithm: i.algorithm, public_jwk: i.public_jwk, public_key_id: i.public_key_id, created_at: i.created_at };
  });
  const [sigStatus, setSigStatus] = useState({});
  const [ready, setReady] = useState(false);

  // Initial load / seed.
  useEffect(() => {
    (async () => {
      let recs = storage.loadRecords();
      if (!recs) {
        recs = await seedDemo();
        storage.saveRecords(recs);
      }
      setRecords(recs);
      setReady(true);
    })();
  }, []);

  // Recompute chain integrity whenever records change.
  useEffect(() => {
    let active = true;
    (async () => {
      const res = await verifyChain(records);
      if (active) setIntegrity(res);
    })();
    return () => {
      active = false;
    };
  }, [records]);

  // Recompute signature statuses whenever records change.
  useEffect(() => {
    let active = true;
    (async () => {
      const map = {};
      for (const r of records) {
        const cur = await computeHash(r);
        const st = await verifySignature(r.signature, cur.hash);
        map[r.id] = st.state;
      }
      if (active) setSigStatus(map);
    })();
    return () => {
      active = false;
    };
  }, [records]);

  // Apply ADA mode + persist settings.
  useEffect(() => {
    storage.saveSettings(settings);
    const root = document.documentElement;
    if (settings.adaMode) root.classList.add("ada-mode");
    else root.classList.remove("ada-mode");
  }, [settings]);

  // One-time migration: move any legacy plaintext private key out of localStorage
  // into the non-extractable IndexedDB key store, then scrub localStorage.
  useEffect(() => {
    const stored = storage.loadIdentity();
    if (stored && stored.private_jwk) {
      (async () => {
        const desc = await importSigningIdentity(stored);
        if (desc) {
          storage.saveIdentity(desc);
          setIdentity(desc);
        } else {
          storage.clearIdentity();
          setIdentity(null);
        }
      })();
    }
  }, []);

  const persist = useCallback((next) => {
    setRecords(next);
    storage.saveRecords(next);
  }, []);

  const lastHash = records.length ? records[records.length - 1].sha256 : GENESIS_HASH;

  const anchorEvent = useCallback(
    async (input) => {
      const prev = records.length ? records[records.length - 1].sha256 : GENESIS_HASH;
      const rec = await createRecord(input, prev);
      persist([...records, rec]);
      return rec;
    },
    [records, persist]
  );

  const deleteRecord = useCallback(
    (id) => {
      persist(records.filter((r) => r.id !== id));
    },
    [records, persist]
  );

  const clearAll = useCallback(() => {
    persist([]);
  }, [persist]);

  const importRecords = useCallback(
    (incoming) => {
      const list = Array.isArray(incoming) ? incoming : [incoming];
      persist([...records, ...list]);
    },
    [records, persist]
  );

  const reseed = useCallback(async () => {
    const recs = await seedDemo();
    persist(recs);
  }, [persist]);

  // DEMO ONLY: mutate a record's payload without rehashing → breaks integrity.
  const tamperRecord = useCallback(
    (id) => {
      const next = records.map((r) =>
        r.id === id
          ? { ...r, payload: r.payload + "\n// [demo tamper injected]", notes: r.notes + " [TAMPERED FOR DEMO]" }
          : r
      );
      persist(next);
    },
    [records, persist]
  );

  const toggleAdaMode = useCallback(() => {
    setSettings((s) => ({ ...s, adaMode: !s.adaMode }));
  }, []);

  const updateThresholds = useCallback((thresholds) => {
    setSettings((s) => ({ ...s, thresholds }));
  }, []);

  const saveHct3Run = useCallback((run) => {
    const summary = {
      convergence: run.cycle3.convergence_pct,
      confidence: run.cycle3.confidence,
      converged: run.cycle3.convergence_reached,
      at: new Date().toISOString(),
    };
    setHct3(summary);
    storage.saveHct3(summary);
  }, []);

  // ===== Signing identity management (private key stays local) =====
  const createIdentity = useCallback(async (label) => {
    const id = await generateIdentity(label);
    setIdentity(id);
    storage.saveIdentity(id);
    return id;
  }, []);

  const importIdentity = useCallback(async (obj) => {
    const descriptor = await importSigningIdentity(obj);
    if (!descriptor) return false;
    setIdentity(descriptor);
    storage.saveIdentity(descriptor);
    return true;
  }, []);

  const clearIdentity = useCallback(async () => {
    setIdentity(null);
    await clearSigningKey();
    storage.clearIdentity();
  }, []);

  // Sign an existing record (functional update to avoid stale closure).
  const signRecord = useCallback(
    async (id) => {
      if (!identity) return null;
      const target = records.find((r) => r.id === id);
      if (!target) return null;
      let signature;
      try {
        signature = await signHash(identity, target.sha256);
      } catch {
        return null;
      }
      setRecords((prev) => {
        const next = prev.map((r) => (r.id === id ? { ...r, signature } : r));
        storage.saveRecords(next);
        return next;
      });
      return signature;
    },
    [identity, records]
  );

  // One-click cross-module workflow on a SINGLE evidence object:
  // Capture → HCT-3 → ADA-4WM → K-Crypto hash → Sign → Anchor → Store.
  const runWorkflow = useCallback(
    async (input) => {
      const prev = records.length ? records[records.length - 1].sha256 : GENESIS_HASH;
      let rec = await createRecord(input, prev);
      let signature = null;
      if (identity) {
        try {
          signature = await signHash(identity, rec.sha256);
        } catch {
          signature = null;
        }
      }
      rec = { ...rec, signature };
      persist([...records, rec]);
      return rec;
    },
    [records, identity, persist]
  );

  const stats = useMemo(() => {
    const total = records.length;
    let verified = 0;
    let failures = 0;
    let firstBreak = -1;
    integrity.forEach((res, i) => {
      const ok = res.selfOk && res.linkOk;
      if (ok) verified += 1;
      else {
        failures += 1;
        if (firstBreak === -1) firstBreak = i;
      }
    });
    const driftAlerts = records.filter((r) => r.drift_status === "WARNING" || r.drift_status === "DRIFT").length;
    const chainHealth = total ? Math.round((verified / total) * 100) : 100;
    return {
      total,
      verified,
      failures,
      driftAlerts,
      chainHealth,
      firstBreak,
      convergence: hct3?.convergence ?? 98.6,
      latest: records[records.length - 1] || null,
      systemStatus: failures > 0 ? "INTEGRITY ALERT" : "OPERATIONAL",
    };
  }, [records, integrity, hct3]);

  const value = {
    records,
    integrity,
    settings,
    stats,
    hct3,
    ready,
    lastHash,
    identity,
    sigStatus,
    anchorEvent,
    deleteRecord,
    clearAll,
    importRecords,
    reseed,
    tamperRecord,
    toggleAdaMode,
    updateThresholds,
    saveHct3Run,
    createIdentity,
    importIdentity,
    clearIdentity,
    signRecord,
    runWorkflow,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
